import React from "react";
import { motion } from "framer-motion";

/**
 * DiasporaRouteMap
 * - Stylised flat-art world map: African continent highlighted in terracotta;
 *   curved dotted travel lines arc from Lagos/Accra/Nairobi/Johannesburg → London/NYC/Toronto/Atlanta.
 * - Pure inline SVG (no external map tiles) so it loads instantly and scales crisp.
 * - Framer Motion draws each route line with stroke-dasharray on mount.
 * - Pulsing city pins.
 *
 * Drop into Landing.jsx wherever you want the "Africa connects to the diaspora" visual.
 */

const ORIGINS = [
  { id: "lagos", x: 510, y: 318, label: "Lagos" },
  { id: "accra", x: 487, y: 320, label: "Accra" },
  { id: "nairobi", x: 600, y: 358, label: "Nairobi" },
  { id: "johannesburg", x: 575, y: 432, label: "Joburg" },
];

const DESTINATIONS = [
  { id: "london", x: 488, y: 195, label: "London" },
  { id: "newyork", x: 270, y: 240, label: "New York" },
  { id: "toronto", x: 245, y: 215, label: "Toronto" },
  { id: "atlanta", x: 250, y: 270, label: "Atlanta" },
];

// Build a slightly arched cubic Bézier between two points.
const arc = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = Math.min(a.y, b.y) - 50; // arch upward
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
};

// Generate origin→destination pairs (every African origin to every Western city).
const ROUTES = [];
ORIGINS.forEach((o, i) => {
  DESTINATIONS.forEach((d, j) => {
    if ((i + j) % 2 === 0) ROUTES.push({ from: o, to: d, delay: (i * 2 + j) * 0.15 });
  });
});

export const DiasporaRouteMap = () => (
  <div className="w-full" data-testid="diaspora-route-map">
    <svg
      viewBox="0 0 1000 600"
      className="w-full h-auto"
      role="img"
      aria-label="World map showing routes from Africa to the diaspora in UK, US and Canada"
    >
      {/* Soft background grid wash */}
      <defs>
        <linearGradient id="oceanWash" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FAF8F5" />
          <stop offset="100%" stopColor="#F3EFE9" />
        </linearGradient>
        <pattern id="dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill="#2A3D36" opacity="0.08" />
        </pattern>
      </defs>
      <rect width="1000" height="600" fill="url(#oceanWash)" />
      <rect width="1000" height="600" fill="url(#dots)" />

      {/* World continents — simplified silhouettes (highly stylised, not topographic) */}
      <g fill="#E8DFD0" stroke="#D9CFBC" strokeWidth="0.8" strokeLinejoin="round">
        {/* North America */}
        <path d="M 90 140 Q 170 110 290 130 Q 360 160 340 240 Q 310 290 240 295 Q 170 300 130 250 Q 80 200 90 140 Z"/>
        {/* South America */}
        <path d="M 260 310 Q 320 330 320 410 Q 300 490 250 510 Q 210 510 220 430 Q 230 350 260 310 Z"/>
        {/* Europe */}
        <path d="M 460 150 Q 540 130 560 200 Q 555 245 500 250 Q 460 245 450 200 Z"/>
        {/* Asia */}
        <path d="M 575 145 Q 760 130 880 190 Q 920 240 860 280 Q 750 295 660 280 Q 590 260 575 200 Z"/>
        {/* Oceania */}
        <path d="M 800 410 Q 870 400 890 450 Q 870 480 820 470 Q 790 455 800 410 Z"/>
      </g>

      {/* AFRICA — highlighted in terracotta */}
      <g>
        <motion.path
          d="M 460 260 Q 530 240 600 270 Q 640 320 620 400 Q 600 470 555 480 Q 510 470 490 430 Q 470 380 460 340 Q 450 290 460 260 Z"
          fill="#C05A3C"
          stroke="#A04A30"
          strokeWidth="1.2"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          style={{ transformOrigin: "540px 370px" }}
        />
        {/* Inner soft kente-inspired stripe accent (very subtle) */}
        <motion.path
          d="M 475 320 L 615 320 M 475 360 L 615 360 M 475 400 L 615 400"
          stroke="#F2C94C"
          strokeWidth="1.5"
          strokeOpacity="0.25"
          strokeDasharray="2 4"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          fill="none"
        />
      </g>

      {/* Routes — animated draw */}
      {ROUTES.map((r, idx) => (
        <motion.path
          key={idx}
          d={arc(r.from, r.to)}
          fill="none"
          stroke="#2A3D36"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeDasharray="3 5"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.7 }}
          transition={{ duration: 2.2, delay: 0.8 + r.delay, ease: "easeInOut" }}
        />
      ))}

      {/* Origin pins (African cities) */}
      {ORIGINS.map((p, i) => (
        <g key={p.id}>
          <motion.circle
            cx={p.x}
            cy={p.y}
            r="6"
            fill="#C05A3C"
            stroke="#FAF8F5"
            strokeWidth="2"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 + i * 0.1 }}
            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
          />
          <motion.circle
            cx={p.x}
            cy={p.y}
            r="6"
            fill="none"
            stroke="#C05A3C"
            strokeWidth="2"
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: [1, 2.4, 1], opacity: [0.8, 0, 0.8] }}
            transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.4, ease: "easeOut" }}
            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
          />
          <text
            x={p.x + 10}
            y={p.y + 4}
            fontSize="11"
            fontFamily="Manrope, system-ui, sans-serif"
            fontWeight="500"
            fill="#1A2024"
          >
            {p.label}
          </text>
        </g>
      ))}

      {/* Destination pins (diaspora cities) */}
      {DESTINATIONS.map((p, i) => (
        <g key={p.id}>
          <motion.circle
            cx={p.x}
            cy={p.y}
            r="5"
            fill="#2A3D36"
            stroke="#FAF8F5"
            strokeWidth="2"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 1.8 + i * 0.12 }}
            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
          />
          <text
            x={p.x + 8}
            y={p.y - 8}
            fontSize="11"
            fontFamily="Manrope, system-ui, sans-serif"
            fontWeight="500"
            fill="#2A3D36"
          >
            {p.label}
          </text>
        </g>
      ))}

      {/* Editorial caption */}
      <text
        x="500"
        y="555"
        textAnchor="middle"
        fontSize="14"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fill="#2A3D36"
      >
        From Lagos to London &nbsp;·&nbsp; From Accra to Atlanta
      </text>
    </svg>
  </div>
);

export default DiasporaRouteMap;
